import { useState, useRef, useCallback, useEffect } from "react";
import {
  Box, Typography, TextField, Button, IconButton, Chip,
  Switch, FormControlLabel, Paper, Stack, Grid, Divider,
  Select, MenuItem, FormControl, InputLabel, Dialog,
  DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Badge, Collapse, InputAdornment, Tabs, Tab,
  Avatar,
} from "@mui/material";
import {
  Add, Delete, Edit, CloudUpload, Close,
  AutoAwesome, CheckCircle, Warning, Image, Tune,
  ExpandMore, ExpandLess, LocalOffer, Inventory2,
  PhotoCamera, Star, ViewModule, Category,
  Info, StoreMallDirectory,
} from "@mui/icons-material";
import apiClient from "../utils/apiClient";
import { useNavigate, useParams } from "react-router-dom";
import { getImageFullUrl, imageBaseurl, pageRoute } from "../utils/common";

// ─── Utilities ────────────────────────────────────────────────────────────────
const uid = () => Math.random().toString(36).slice(2, 10);

function cartesian(arrays) {
  if (!arrays.length) return [[]];
  return arrays.reduce(
    (acc, arr) => acc.flatMap((a) => arr.map((b) => [...a, b])),
    [[]]
  );
}

const toSlug = (str) =>
  str.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

// FIX: generate a readable SKU string instead of Math.random()
const generateSku = (combo) =>
  combo
    .map((c) => `${c.name.slice(0, 3).toUpperCase()}-${c.val.slice(0, 4).toUpperCase()}`)
    .join("-");

// ─── Image Uploader ───────────────────────────────────────────────────────────
function ImageUploader({ images, onAdd, onRemove, onSetThumbnail, thumbnailId, label = "Images", coverImage = false }) {
  const inputRef = useRef();
  const [dragging, setDragging] = useState(false);

  const handleFiles = (files) => {
    Array.from(files).forEach((file) => {
      if (!file.type.startsWith("image/")) return;
      // FIX: store objectUrl for preview, keep file reference separately
      onAdd({
        id: uid(),
        previewUrl: URL.createObjectURL(file),
        file,
        name: file.name,
      });
    });
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <Box>
      <Typography
        variant="body2"
        fontWeight={700}
        mb={1.5}
        sx={{ color: "text.secondary", letterSpacing: 0.5, textTransform: "uppercase", fontSize: 11 }}
      >
        {label}
      </Typography>

      <Box
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current.click()}
        sx={{
          border: "2px dashed",
          borderColor: dragging ? "primary.main" : "divider",
          borderRadius: 2, p: 3, textAlign: "center", cursor: "pointer",
          bgcolor: dragging ? "primary.50" : "grey.50", transition: "all 0.2s", mb: 2,
          "&:hover": { borderColor: "primary.light", bgcolor: "primary.50" },
        }}
      >
        <CloudUpload sx={{ fontSize: 36, color: dragging ? "primary.main" : "grey.400", mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          Drag & drop images or <b style={{ color: "#1976d2" }}>browse</b>
        </Typography>
        <Typography variant="caption" color="text.disabled">PNG, JPG, WEBP up to 5MB</Typography>
        <input ref={inputRef} type="file" multiple accept="image/*"
          style={{ display: "none" }} onChange={(e) => handleFiles(e.target.files)} />
      </Box>

      {images.length > 0 && (
        <Grid container spacing={1.5}>
          {images.map((img) => (
            <Grid item size={{ xs: 4, sm: 3, md: 2 }} key={img.id}>
              <Box
                sx={{
                  position: "relative", borderRadius: 2, overflow: "hidden",
                  border: "2px solid",
                  // FIX: compare by id, not by url (objectUrls can be recreated)
                  borderColor: thumbnailId === img.id ? "primary.main" : "divider",
                  aspectRatio: "1",
                  "&:hover .img-actions": { opacity: 1 },
                }}
              >
                <img src={img.previewUrl} alt={img.name}
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                {thumbnailId === img.id && (
                  <Chip label="Cover" color="primary"
                    sx={{ position: "absolute", top: 4, left: 4, fontSize: 9, height: 18, px: 0.5 }} />
                )}
                <Box
                  className="img-actions"
                  sx={{
                    position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,0.55)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    gap: 0.5, opacity: 0, transition: "opacity 0.2s",
                  }}
                >
                  {coverImage &&
                    <Tooltip title="Set as cover">
                      <IconButton sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.15)" }}
                        onClick={() => onSetThumbnail(img.id)}>
                        <Star fontSize="small" />
                      </IconButton>
                    </Tooltip>}

                  <Tooltip title="Remove">
                    <IconButton sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.15)" }}
                      onClick={() => onRemove(img.id)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

// ─── Specifications Editor ────────────────────────────────────────────────────
function SpecificationsEditor({ specs, onChange }) {
  const [key, setKey] = useState("");
  const [val, setVal] = useState("");

  const add = () => {
    if (!key.trim()) return;
    onChange([...specs, { id: uid(), key: key.trim(), value: val.trim() }]);
    setKey(""); setVal("");
  };
  const remove = (id) => onChange(specs.filter((s) => s.id !== id));
  const update = (id, field, v) =>
    onChange(specs.map((s) => (s.id === id ? { ...s, [field]: v } : s)));

  return (
    <Box>
      <Stack direction="row" spacing={1} mb={1.5}>
        <TextField label="Key" placeholder="e.g. Material"
          value={key} onChange={(e) => setKey(e.target.value)} sx={{ flex: 1 }} />
        <TextField label="Value" placeholder="e.g. Cotton"
          value={val} onChange={(e) => setVal(e.target.value)} sx={{ flex: 1 }}
          onKeyDown={(e) => e.key === "Enter" && add()} />
        <Button variant="outlined" onClick={add} startIcon={<Add />}>Add</Button>
      </Stack>
      <Stack spacing={0.8}>
        {specs.map((s) => (
          <Stack key={s.id} direction="row" spacing={1} alignItems="center">
            <TextField value={s.key} sx={{ flex: 1 }}
              onChange={(e) => update(s.id, "key", e.target.value)} />
            <TextField value={s.value} sx={{ flex: 2 }}
              onChange={(e) => update(s.id, "value", e.target.value)} />
            <IconButton color="error" onClick={() => remove(s.id)}>
              <Delete fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

// ─── Attribute Section ────────────────────────────────────────────────────────
function AttributeSection({ attributes, onChange }) {
  const [newName, setNewName] = useState("");
  const [newVal, setNewVal] = useState({});

  const addAttr = () => {
    const name = newName.trim();
    if (!name || attributes.find((a) => a.name.toLowerCase() === name.toLowerCase())) return;
    onChange([...attributes, { id: uid(), name, position: attributes.length, values: [] }]);
    setNewName("");
  };

  const removeAttr = (id) => onChange(attributes.filter((a) => a.id !== id));

  const addVal = (attrId) => {
    const v = (newVal[attrId] || "").trim();
    if (!v) return;
    onChange(
      attributes.map((a) =>
        a.id === attrId && !a.values.find((x) => x.value.toLowerCase() === v.toLowerCase())
          ? { ...a, values: [...a.values, { id: uid(), value: v }] }
          : a
      )
    );
    setNewVal((p) => ({ ...p, [attrId]: "" }));
  };

  const removeVal = (attrId, valId) =>
    onChange(
      attributes.map((a) =>
        a.id === attrId ? { ...a, values: a.values.filter((v) => v.id !== valId) } : a
      )
    );

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <Tune fontSize="small" color="primary" />
        <Typography fontWeight={700} fontSize={14}>Variant Attributes</Typography>
        <Typography variant="caption" color="text.secondary">e.g. Color, Size, Material</Typography>
      </Stack>
      <Stack direction="row" spacing={1} mb={2.5}>
        <TextField label="Attribute name" value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addAttr()} sx={{ flex: 1 }} />
        <Button variant="contained" onClick={addAttr} startIcon={<Add />}>Add</Button>
      </Stack>
      <Stack spacing={2}>
        {attributes.map((attr) => (
          <Box key={attr.id} sx={{ pl: 2, borderLeft: "3px solid", borderColor: "primary.main", borderRadius: "0 4px 4px 0" }}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <Typography fontWeight={700} fontSize={13}>{attr.name}</Typography>
              <Chip label={`${attr.values.length} values`} sx={{ fontSize: 10, height: 18 }} />
              <Box flex={1} />
              <IconButton color="error" onClick={() => removeAttr(attr.id)}>
                <Delete fontSize="small" />
              </IconButton>
            </Stack>
            <Stack direction="row" flexWrap="wrap" gap={0.8} mb={1}>
              {attr.values.map((v) => (
                <Chip key={v.id} label={v.value} onDelete={() => removeVal(attr.id, v.id)} />
              ))}
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField placeholder={`Add ${attr.name} value`} sx={{ maxWidth: 200 }}
                value={newVal[attr.id] || ""}
                onChange={(e) => setNewVal((p) => ({ ...p, [attr.id]: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && addVal(attr.id)} />
              <Button variant="outlined" onClick={() => addVal(attr.id)}>Add</Button>
            </Stack>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}

// ─── Variant Edit Dialog ──────────────────────────────────────────────────────
function VariantEditDialog({ open, variant, onClose, onSave, deletedVariantImages, setDeletedVariantImages }) {
  const [form, setForm] = useState({});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { setForm(variant || {}); }, [variant]);

  const handleImageFiles = (files) => {
    Array.from(files).forEach((file) => {
      if (!file.type.startsWith("image/")) return;
      const img = { id: uid(), previewUrl: URL.createObjectURL(file), file, name: file.name };
      setForm((prev) => ({
        ...prev,
        images: [...(prev.images || []), img],
        // auto-set thumbnail to first uploaded image if none set
        thumbnailId: prev.thumbnailId || img.id,
      }));
    });
  };

  if (!variant) return null;

  const label = (form.attributes || [])
    .map((a) => `${a.attributeName}: ${a.attributeValue}`)
    .join(" / ");

  const thumbImg = (form.images || []).find((i) => i.id === form.thumbnailId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <Edit fontSize="small" color="primary" />
            <Box>
              <Typography fontWeight={700} fontSize={15}>Edit Variant</Typography>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose}><Close /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          <Grid item size={{ xs: 12 }}>
            <TextField fullWidth label="SKU *" value={form.sku || ""}
              onChange={(e) => set("sku", e.target.value)} />
          </Grid>
          <Grid item size={{ xs: 6 }}>
            <TextField fullWidth label="Price *" type="number"
              value={form.price || ""} onChange={(e) => set("price", e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
          </Grid>
          <Grid item size={{ xs: 6 }}>
            <TextField fullWidth label="Old Price" type="number"
              value={form.oldPrice || ""} onChange={(e) => set("oldPrice", e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
          </Grid>
          <Grid item size={{ xs: 6 }}>
            <TextField fullWidth label="Stock" type="number"
              value={form.stock ?? 0} onChange={(e) => set("stock", Number(e.target.value))} />
          </Grid>

          {/* <Grid item size={{ xs: 4 }}>
            <TextField fullWidth label="Weight (kg)" type="number"
              value={form.weight || ""} onChange={(e) => set("weight", e.target.value)} />
          </Grid> */}
          <Grid item size={{ xs: 4 }}>
            <TextField fullWidth label="Unit"
              value={form.unit || "piece"} onChange={(e) => set("unit", e.target.value)} />
          </Grid>
          <Grid item size={{ xs: 4 }}>
            <TextField fullWidth label="Unit Qty" type="number"
              value={form.unitQty ?? 1} onChange={(e) => set("unitQty", e.target.value)} />
          </Grid>
          <Grid item size={{ xs: 12 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={form.status || "active"}
                onChange={(e) => set("status", e.target.value)}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item size={{ xs: 12 }}>
            <Divider sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">Variant Images</Typography>
            </Divider>
            <ImageUploader

              images={form.images || []}
              thumbnailId={form.thumbnailId}
              label="Variant Images"
              onAdd={(img) => setForm((prev) => ({
                ...prev,
                images: [...(prev.images || []), img],
                thumbnailId: prev.thumbnailId || img.id,
              }))}
              onRemove={(id) => {
                const remaining = (form.images || []).filter((i) => i.id !== id);

                const img = form.images.find((i) => i.id === id);

                if (img?.isExisting) {
                  setDeletedVariantImages((prev) => [...prev, img.name]);
                }
                setForm((prev) => ({
                  ...prev,
                  images: remaining,
                  thumbnailId: prev.thumbnailId === id ? (remaining[0]?.id || "") : prev.thumbnailId,
                }));
              }}
              onSetThumbnail={(id) => set("thumbnailId", id)}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button variant="contained" startIcon={<CheckCircle />}
          onClick={() => { onSave(form); onClose(); }}>
          Save Variant
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Variants Table ───────────────────────────────────────────────────────────
function VariantsTable({ variants, onEdit, onDelete, onBulkPrice }) {
  const [open, setOpen] = useState(true);
  const hasIssue = (v) => !v.sku || !v.price;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
      <Stack
        direction="row" alignItems="center" justifyContent="space-between"
        sx={{ px: 2.5, py: 1.5, bgcolor: "grey.50", cursor: "pointer" }}
        onClick={() => setOpen((o) => !o)}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Inventory2 fontSize="small" color="primary" />
          <Typography fontWeight={700} fontSize={14}>Variants</Typography>
          <Badge badgeContent={variants.length} color="primary" />
          {variants.some(hasIssue) && (
            <Chip icon={<Warning />} label="Incomplete" color="warning" />
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Set same price for all variants">
            <Button startIcon={<LocalOffer />} onClick={() => {
              const p = prompt("Set price for ALL variants:");
              if (p && !isNaN(p)) onBulkPrice(parseFloat(p));
            }}>Bulk Price</Button>
          </Tooltip>
          {open ? <ExpandLess /> : <ExpandMore />}
        </Stack>
      </Stack>

      <Collapse in={open}>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, fontSize: 12, bgcolor: "white" } }}>
                <TableCell>Combination</TableCell>
                <TableCell>SKU</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Old Price</TableCell>
                <TableCell>Stock</TableCell>
                <TableCell>Images</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {variants.map((v, i) => {
                const thumbImg = (v.images || []).find((img) => img.id === v.thumbnailId);
                return (
                  <TableRow key={v.id}
                    sx={{ bgcolor: hasIssue(v) ? "#fffbeb" : i % 2 ? "grey.50" : "white", "&:hover": { bgcolor: "primary.50" } }}>
                    <TableCell>
                      <Stack spacing={0.4}>
                        {(v.attributes || []).map((a) => (
                          <Chip key={a.attributeName}
                            label={`${a.attributeName}: ${a.attributeValue}`}
                            sx={{ fontSize: 10, height: 18 }} />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography fontSize={12} fontFamily="monospace"
                        color={!v.sku ? "error.main" : "text.primary"}>
                        {v.sku || "Missing"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontSize={12} color={!v.price ? "error.main" : "text.primary"}>
                        {v.price ? `₹${parseFloat(v.price).toFixed(2)}` : "Missing"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontSize={12} color="text.secondary">
                        {v.oldPrice ? `₹${parseFloat(v.oldPrice).toFixed(2)}` : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={v.stock ?? 0}
                        color={(v.stock ?? 0) === 0 ? "error" : (v.stock ?? 0) < 5 ? "warning" : "success"} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {(v.images || []).length > 0
                          ? <Avatar src={v.images[0]?.previewUrl} variant="rounded" sx={{ width: 28, height: 28 }} />
                          : <Avatar variant="rounded" sx={{ width: 28, height: 28, bgcolor: "grey.200" }}>
                            <Image sx={{ fontSize: 14, color: "grey.500" }} />
                          </Avatar>
                        }
                        {/* {thumbImg
                          ? <Avatar src={thumbImg.previewUrl} variant="rounded" sx={{ width: 28, height: 28 }} />
                          : <Avatar variant="rounded" sx={{ width: 28, height: 28, bgcolor: "grey.200" }}>
                            <Image sx={{ fontSize: 14, color: "grey.500" }} />
                          </Avatar>
                        } */}
                        {(v.images || []).length > 0 && (
                          <Typography fontSize={10} color="text.secondary">+{v.images.length}</Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip label={v.status || "active"}
                        color={v.status === "inactive" ? "default" : "success"} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row">
                        <Tooltip title="Edit">
                          <IconButton onClick={() => onEdit(v)}><Edit fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton color="error" onClick={() => onDelete(v.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {variants.length === 0 && (
          <Box py={5} textAlign="center">
            <Inventory2 sx={{ fontSize: 40, color: "grey.300", mb: 1 }} />
            <Typography color="text.secondary" fontSize={13}>
              Generate variants from attributes above
            </Typography>
          </Box>
        )}
      </Collapse>
    </Paper>
  );
}

// ─── Section Wrapper ──────────────────────────────────────────────────────────
function Section({ icon, title, subtitle, children, accent = false }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: "hidden", borderColor: accent ? "primary.light" : "divider" }}>
      <Box sx={{ px: 3, py: 2, borderBottom: "1px solid", borderColor: "divider", bgcolor: accent ? "primary.50" : "grey.50" }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ color: accent ? "primary.main" : "text.secondary" }}>{icon}</Box>
          <Box>
            <Typography fontWeight={700} fontSize={15}>{title}</Typography>
            {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
          </Box>
        </Stack>
      </Box>
      <Box sx={{ p: 3 }}>{children}</Box>
    </Paper>
  );
}

function TabPanel({ value, index, children }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
export default function ProductManager({ mode = 'add' }) {
  const [tab, setTab] = useState(0);
  const { productId } = useParams();
  const [loading, setLoading] = useState(false);

  const [product, setProduct] = useState({
    hsnCode:'',
    gstPercent:0,
    igstPercent:0,
    name: "",
    productCode: new Date() + Math.random,
    // slug: "",
    description: "",
    price: "",
    oldPrice: "",
    categoryId: "",
    subCategoryId: "",
    thirdCategoryId: "",
    brand: "",
    brandId: "",
    stock: 0,
    unit: "piece",
    unitQty: 1,
    weight: "",
    status: "active",
    isProductVariants: false,
  });

  const setField = (k, v) => setProduct((p) => ({ ...p, [k]: v }));

  // FIX: track thumbnailId (image id) instead of thumbnailUrl
  const [images, setImages] = useState(
    []
  );
  const [deletedImages, setDeletedImages] = useState([]);
  const [deletedVariantImages, setDeletedVariantImages] = useState([]);
  const [thumbnailId, setThumbnailId] = useState("");

  const [specs, setSpecs] = useState(
    []
  );
  const [attributes, setAttributes] = useState([]);
  const [variants, setVariants] = useState([]);
  const [editVariant, setEditVariant] = useState(null);
  const [brands, setBrands] = useState([]);

  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [thirdCategories, setThirdCategories] = useState([]);

  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });
  const toast = (msg, severity = "success") => setSnack({ open: true, msg, severity });

  // Fetch brand on mount
  const fetchBrands = async () => {
    try {
      const res = await apiClient.get("/api/brand");
      setBrands(res?.data?.data || []);
    } catch (err) {
      toast.error("Failed to load brand");
    }
  };
  useEffect(() => {
    apiClient.get("/api/categories")
      .then((res) => setCategories(res?.data?.data || []))
      .catch(() => { });
    fetchBrands()
  }, []);

  // Auto-set thumbnail to first image if none selected
  useEffect(() => {
    if (!thumbnailId && images.length > 0) {
      setThumbnailId(images[0].id);
    }
  }, [images]);

  const handleName = (v) => {
    setField("name", v);
    // if (mode === 'add') setField("slug", toSlug(v));
  };

  // FIX: generate proper SKU strings, preserve existing variants on regenerate
  const generateVariants = useCallback(() => {
    const valid = attributes.filter((a) => a.values.length > 0);
    if (!valid.length) { toast("Add at least one attribute with values.", "warning"); return; }

    const combos = cartesian(valid.map((a) => a.values.map((v) => ({ name: a.name, val: v.value }))));

    const next = combos.map((combo) => {
      const key = combo.map((c) => `${c.name}:${c.val}`).join("|");
      const existing = variants.find(
        (v) =>
          (v.attributes || []).map((a) => `${a.attributeName}:${a.attributeValue}`).join("|") === key
      );
      if (existing) return existing;

      return {
        id: uid(),
        // FIX: readable SKU string, not Math.random()
        sku: generateSku(combo),
        price: Number(product.price) || 0,
        oldPrice: Number(product.oldPrice) || 0,
        stock: 0,
        unit: product.unit,
        unitQty: product.unitQty,
        weight: product.weight || "",
        status: "active",
        images: [],
        thumbnailId: "",
        attributes: combo.map((c) => ({ attributeName: c.name, attributeValue: c.val })),
      };
    });

    setVariants(next);
    toast(`${next.length} variant combination${next.length !== 1 ? "s" : ""} generated.`);
  }, [attributes, variants, product]);

  // ── Validation ──────────────────────────────────────────────────────────────
  const validate = () => {
    if (!product.name.trim()) { toast("Product name is required.", "error"); setTab(0); return false; }
    if (!product.hsnCode.trim()) { toast("Product HSN code is required.", "error"); setTab(0); return false; }
    // if (!product.slug.trim()) { toast("Slug is required.", "error"); setTab(0); return false; }
    if (!product.categoryId) { toast("Category is required.", "error"); setTab(0); return false; }

    if (product.isProductVariants) {
      if (!variants.length) { toast("Generate variants first.", "error"); setTab(3); return false; }
      const bad = variants.filter((v) => !v.sku || !v.price);
      if (bad.length) { toast(`${bad.length} variant(s) missing SKU or price.`, "warning"); setTab(3); return false; }
    } else {
      if (!product.price) { toast("Price is required.", "error"); setTab(0); return false; }
      if (isNaN(Number(product.price)) || Number(product.price) <= 0) {
        toast("Price must be a positive number.", "error"); setTab(0); return false;
      }
    }
    return true;
  };

  const navigate = useNavigate()

  const pageChange = (url) => {
    navigate(url)
  }

  const handleUpdate = async () => {
    if (!validate()) return;
    setLoading(true);

    const formData = new FormData();
    // Basic fields
    Object.entries(product).forEach(([key, value]) => {
      formData.append(key, String(value));
    });

    // Specs
    const specsObj = specs.reduce((acc, s) => ({ ...acc, [s.key]: s.value }), {});
    formData.append("specifications", JSON.stringify(specsObj));

    // Attributes
    formData.append("attributes", JSON.stringify(attributes));

    // 🔥 NEW IMAGES ONLY
    images.forEach((img) => {
      if (!img.isExisting && img.file) {
        formData.append("images", img.file);
      }
    });

    // 🔥 DELETED IMAGES
    formData.append("deletedImages", JSON.stringify(deletedImages));

    // 🔥 THUMBNAIL
    const thumb = images.find((i) => i.id === thumbnailId);

    if (thumb?.file) {
      formData.append("thumbnail", thumb.file);
    } else if (thumb?.isExisting) {
      formData.append("thumbnail", thumb.name);
    }

    // 🔥 VARIANTS
    const variantsPayload = variants.map((v) => ({
      id: v.id,
      sku: v.sku,
      price: v.price,
      oldPrice: v.oldPrice,
      stock: v.stock,
      unit: v.unit,
      unitQty: v.unitQty,
      weight: v.weight,
      status: v.status,
      attributes: v.attributes,
      imageCount: (v.images || []).filter((i) => !i.isExisting && i.file).length,
    }));

    formData.append("variants", JSON.stringify(variantsPayload));

    // 🔥 VARIANT NEW IMAGES
    variants.forEach((v) => {
      (v.images || [])
        .filter((i) => !i.isExisting && i.file)
        .forEach((img) => {
          formData.append("variantImages", img.file);
        });
    });

    // 🔥 VARIANT DELETED IMAGES
    formData.append("deletedVariantImages", JSON.stringify(deletedVariantImages));

    try {
      await apiClient.put(`/api/new-products/${productId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast("Product updated successfully 🚀");
      // ✅ REDIRECT
      setTimeout(() => {
        navigate(pageRoute.productList);
      }, 800);

    } catch (err) {
      toast(err.response?.data?.message || "Update failed", "error");
    }
    setLoading(false);

  };

  // ── Submit ──────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);

    const formData = new FormData();

    // Basic scalar fields
    Object.entries(product).forEach(([key, value]) => {
      // FIX: FormData needs strings; booleans must be "true"/"false"
      formData.append(key, String(value));
    });

    // FIX: specifications must be JSON-stringified
    const specsObj = specs.reduce((acc, s) => ({ ...acc, [s.key]: s.value }), {});
    formData.append("specifications", JSON.stringify(specsObj));

    // FIX: attributes JSON
    formData.append("attributes", JSON.stringify(attributes));

    // Product images (new files only; existing urls handled separately if editing)
    images.forEach((img) => {
      if (img.file) formData.append("images", img.file);
    });

    // FIX: thumbnail — send the actual file of the selected thumbnail image
    const thumbImg = images.find((i) => i.id === thumbnailId);
    if (thumbImg?.file) {
      formData.append("thumbnail", thumbImg.file);
    }

    // Variants — JSON payload with imageCount for backend mapping
    const variantsPayload = variants.map((v) => {
      const thumbImg2 = (v.images || []).find((i) => i.id === v.thumbnailId);

      return {
        id: v.id,
        sku: v.sku,
        price: v.price,
        oldPrice: v.oldPrice,
        stock: v.stock,
        unit: v.unit,
        unitQty: v.unitQty,
        weight: v.weight,
        status: v.status,
        attributes: v.attributes,

        // ✅ IMPORTANT
        thumbnail: thumbImg2?.isExisting ? thumbImg2.name : null,

        imageCount: (v.images || []).filter((i) => !i.isExisting && i.file).length,
      };
    });

    formData.append("variants", JSON.stringify(variantsPayload));

    // FIX: variant images — only append file objects, in order matching imageCount
    variants.forEach((v) => {
      const thumbImg = (v.images || []).find(
        (i) => i.id === v.thumbnailId && i.file
      );

      const otherImgs = (v.images || []).filter(
        (i) => i.file && i.id !== v.thumbnailId
      );

      const orderedImgs = thumbImg ? [thumbImg, ...otherImgs] : otherImgs;

      orderedImgs.forEach((img) => {
        formData.append("variantImages", img.file);
      });
    });

    try {
      await apiClient.post("/api/new-products", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast("Product saved successfully!");
      // ✅ REDIRECT
      setTimeout(() => {
        navigate(pageRoute.productList);
      }, 800);
    } catch (err) {
      toast(err.response?.data?.message || "Server error!", "error");
    }
    setLoading(false);

  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/api/new-products/${productId}`);
      const prod = res?.data?.data;

      // ✅ BASIC
      setProduct({
        name: prod?.name || "",
        productCode: prod?.productCode || "",
        hsnCode: prod?.hsnCode || "",
        gstPercent: prod?.gstPercent || 0,
        igstPercent: prod?.igstPercent || 0,
        // slug: prod?.slug || "",
        description: prod?.description || "",
        price: prod?.price || "",
        oldPrice: prod?.oldPrice || "",
        categoryId: prod?.categoryId || "",
        subCategoryId: prod?.subCategoryId || "",
        thirdCategoryId: prod?.thirdCategoryId || "",
        brandId: prod?.brandId || "",
        stock: prod?.stock || 0,
        unit: prod?.unit || "piece",
        unitQty: prod?.unitQty || 1,
        weight: prod?.weight || "",
        status: prod?.status || "active",
        isProductVariants: prod?.isProductVariants || false,
      });

      // ✅ IMAGES FIX
      const productImages =
        typeof prod.images === "string"
          ? JSON.parse(prod.images)
          : prod.images || [];

      const mappedImages = productImages.map((url) => ({
        id: uid(),
        previewUrl: getImageFullUrl(url),
        file: null,
        name: url,
        isExisting: true, // ✅ mark existing

      }));

      setImages(mappedImages);

      const thumb = mappedImages.find((img) => img.name === prod.thumbnail);
      setThumbnailId(thumb?.id || "");

      // ✅ SPECS FIX
      const specsData =
        typeof prod.specifications === "string"
          ? JSON.parse(prod.specifications)
          : prod.specifications || {};

      setSpecs(
        Object.entries(specsData).map(([key, value]) => ({
          id: uid(),
          key,
          value,
        }))
      );

      // ✅ ATTRIBUTES
      setAttributes(prod?.attributes || []);

      // ✅ VARIANTS FIX
      const parsedVariants = (prod.variants || []).map((v) => {
        const imgs =
          typeof v.images === "string"
            ? JSON.parse(v.images)
            : v.images || [];

        const mappedImgs = imgs.map((url) => ({
          id: uid(),
          previewUrl: getImageFullUrl(url),
          file: null,
          name: url,
          isExisting: true, // ✅ mark existing

        }));

        const thumb = mappedImgs.find((img) => img.name === v.thumbnail);

        return {
          ...v,
          images: mappedImgs,
          thumbnailId: thumb?.id || "",
        };
      });

      setVariants(parsedVariants);

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (productId) {
      fetchProducts()
    }
  }, [productId]);

  useEffect(() => {
    if (product.categoryId && categories.length > 0) {
      const selectedCategory = categories.find(
        (c) => c.id === product.categoryId
      );

      const subs = selectedCategory?.subcategories || [];
      setSubCategories(subs);

      // ✅ ALSO auto set third categories if subCategory already exists
      if (product.subCategoryId) {
        const selectedSub = subs.find(
          (s) => s.id === product.subCategoryId
        );
        setThirdCategories(selectedSub?.subcategories || []);
      }
    }
  }, [product.categoryId, categories]);

  return (
    <Paper>
      <Box sx={{ mx: "auto", p: { xs: 2, md: 3 } }}>

        {/* Header */}
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }} mb={3} spacing={2}>
          <Box>
            <Typography variant="h5" fontWeight={900} letterSpacing={-0.5}>
              {mode != 'add' ? "Edit Product" : "New Product"}
            </Typography>
            <Typography color="text.secondary" fontSize={13}>
              Fill in all tabs to create a complete product listing
            </Typography>
          </Box>

        </Stack>

        {/* Tabs */}
        <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: "hidden", mb: 3 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
            sx={{ borderBottom: "1px solid", borderColor: "divider", bgcolor: "grey.50" }}>
            <Tab label="Basic Info" icon={<Info fontSize="small" />} iconPosition="start" />
            <Tab label="Images & Media" icon={<Image fontSize="small" />} iconPosition="start" />
            <Tab label="Specifications" icon={<ViewModule fontSize="small" />} iconPosition="start" />
            <Tab
              label={
                <Stack direction="row" spacing={2} alignItems="center">
                  <span>Variants {" "}</span>
                  {variants.length > 0 && <Badge badgeContent={variants.length} color="primary" sx={{ ml: 1 }} />}
                </Stack>
              }
              icon={<Tune fontSize="small" />} iconPosition="start"
            />
          </Tabs>
        </Paper>

        {/* TAB 0 — Basic Info */}
        <TabPanel value={tab} index={0}>
          <Stack spacing={3}>
            <Section icon={<StoreMallDirectory />} title="Product Identity" subtitle="Core product details">
              <Grid container spacing={2}>
                <Grid item size={{ xs: 12, md: 6 }}>
                  <TextField fullWidth label="Product Name *"
                    value={product.name} onChange={(e) => handleName(e.target.value)} />
                </Grid>
                {/* <Grid item size={{ xs: 6, md: 6 }}>
                  <TextField fullWidth label="Product Code *"
                    value={product.productCode} onChange={(e) => setField("productCode", e.target.value)} />
                </Grid> */}
                <Grid item size={{ xs: 6, md: 6 }}>
                  <TextField fullWidth label="HSN Code *"
                    value={product.hsnCode} onChange={(e) => setField("hsnCode", e.target.value)} />
                </Grid>
                <Grid item size={{ xs: 6, md: 6 }}>
                  <TextField fullWidth label="GST Percent" type="number"
                    value={product.gstPercent} onChange={(e) => setField("gstPercent", e.target.value)} />
                </Grid>
                <Grid item size={{ xs: 6, md: 6 }}>
                  <TextField fullWidth label="IGST Percent" type="number"
                    value={product.igstPercent} onChange={(e) => setField("igstPercent", e.target.value)} />
                </Grid>
                <Grid item size={{ xs: 6, md: 6 }}>
                  <FormControl fullWidth >
                    <InputLabel>Status</InputLabel>
                    <Select label="Status" value={product.status} onChange={(e) => setField("status", e.target.value)}>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {/* <Grid item size={{ xs: 12 }}>
                  <TextField fullWidth label="Slug *"
                    value={product.slug} onChange={(e) => setField("slug", e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography fontSize={12} color="text.disabled">/products/</Typography>
                        </InputAdornment>
                      ),
                    }} />
                </Grid> */}
                <Grid item size={{ xs: 12 }}>
                  <TextField fullWidth label="Description" multiline rows={4}
                    value={product.description} onChange={(e) => setField("description", e.target.value)} />
                </Grid>
                <Grid item size={{ xs: 6, md: 4 }}>
                  <TextField
                    select
                    // margin="normal"
                    label="Brand"
                    name="brandId"
                    fullWidth
                    value={product?.brandId}
                    onChange={(e) => {
                      setField('brandId',
                        e.target.value
                      );
                    }}
                  // error={!!errors.brandId}
                  // helperText={errors.brandId}
                  >
                    <MenuItem value="">None</MenuItem>
                    {brands.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  {/* <TextField fullWidth label="Brand"
                    value={product.brand} onChange={(e) => setField("brand", e.target.value)} /> */}
                </Grid>
                <Grid item size={{ xs: 6, md: 4 }}>
                  <TextField fullWidth label="Unit" placeholder="piece, kg, litre"
                    value={product.unit} onChange={(e) => setField("unit", e.target.value)} />
                </Grid>
                {/* <Grid item size={{ xs: 6, md: 4 }}>
                  <TextField fullWidth label="Weight (kg)" type="number"
                    value={product.weight} onChange={(e) => setField("weight", e.target.value)} />
                </Grid> */}
              </Grid>
            </Section>

            <Section icon={<Category />} title="Category" subtitle="Where this product lives">
              <Grid container spacing={2}>
                <Grid item size={{ xs: 12, md: 4 }}>
                  <TextField select label="Category *" fullWidth value={product.categoryId}
                    onChange={(e) => {
                      const selected = categories.find((c) => c.id === e.target.value);
                      setProduct((p) => ({ ...p, categoryId: e.target.value, subCategoryId: "", thirdCategoryId: "" }));
                      setSubCategories(selected?.subcategories || []);
                      setThirdCategories([]);
                    }}>
                    <MenuItem value="">None</MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item size={{ xs: 12, md: 4 }}>
                  {subCategories.length > 0 && (
                    <TextField select label="Sub Category" fullWidth value={product.subCategoryId}
                      onChange={(e) => {
                        const selected = subCategories.find((s) => s.id === e.target.value);
                        setProduct((p) => ({ ...p, subCategoryId: e.target.value, thirdCategoryId: "" }));
                        setThirdCategories(selected?.subcategories || []);
                      }}>
                      <MenuItem value="">None</MenuItem>
                      {subCategories.map((sub) => (
                        <MenuItem key={sub.id} value={sub.id}>{sub.name}</MenuItem>
                      ))}
                    </TextField>
                  )}
                </Grid>
                <Grid item size={{ xs: 12, md: 4 }}>
                  {thirdCategories.length > 0 && (
                    <TextField select label="Third Category" fullWidth value={product.thirdCategoryId}
                      onChange={(e) => setProduct((p) => ({ ...p, thirdCategoryId: e.target.value }))}>
                      <MenuItem value="">None</MenuItem>
                      {thirdCategories.map((third) => (
                        <MenuItem key={third.id} value={third.id}>{third.name}</MenuItem>
                      ))}
                    </TextField>
                  )}
                </Grid>
              </Grid>
            </Section>

            <Section icon={<LocalOffer />} title="Pricing & Stock"
              subtitle={product.isProductVariants ? "Base reference — per-variant pricing in Variants tab" : "Simple product pricing"}>
              <Stack direction="row" justifyContent="flex-end" mb={2}>
                <FormControlLabel
                  control={<Switch checked={product.isProductVariants}
                    onChange={(e) => setField("isProductVariants", e.target.checked)} color="primary" />}
                  label={<Typography fontSize={13} fontWeight={600}>Has Variants</Typography>}
                />
              </Stack>
              <Grid container spacing={2}>
                <Grid item size={{ xs: 6, md: 3 }}>
                  <TextField fullWidth
                    label={product.isProductVariants ? "Base Price (ref)" : "Price *"}
                    type="number" value={product.price}
                    onChange={(e) => setField("price", e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                </Grid>
                <Grid item size={{ xs: 6, md: 3 }}>
                  <TextField fullWidth label="Old Price (MRP)" type="number"
                    value={product.oldPrice} onChange={(e) => setField("oldPrice", e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                </Grid>
                {!product.isProductVariants && (
                  <>
                    <Grid item size={{ xs: 6, md: 3 }}>
                      <TextField fullWidth label="Stock" type="number"
                        value={product.stock} onChange={(e) => setField("stock", Number(e.target.value))} />
                    </Grid>

                    <Grid item size={{ xs: 6, md: 3 }}>
                      <TextField fullWidth label="Unit Qty" type="number"
                        value={product.unitQty} onChange={(e) => setField("unitQty", e.target.value)} />
                    </Grid>
                  </>
                )}
              </Grid>
            </Section>
          </Stack>
        </TabPanel>

        {/* TAB 1 — Images & Media */}
        <TabPanel value={tab} index={1}>
          <Stack spacing={3}>
            <Section icon={<PhotoCamera />} title="Product Images"
              subtitle="Set a cover image — it becomes the product thumbnail">
              <ImageUploader
                coverImage={true}
                images={images}
                thumbnailId={thumbnailId}
                label="Product Images"
                onAdd={(img) => setImages((p) => [...p, img])}
                onRemove={(id) => {
                  setImages((prev) => {
                    const img = prev.find((i) => i.id === id);

                    // 🔥 अगर पुरानी image है तो track करो
                    if (img?.isExisting) {
                      setDeletedImages((d) => [...d, img.name]);
                    }

                    return prev.filter((i) => i.id !== id);
                  });
                  setImages((p) => p.filter((i) => i.id !== id));
                  if (thumbnailId === id) {
                    const remaining = images.filter((i) => i.id !== id);
                    setThumbnailId(remaining[0]?.id || "");
                  }

                }}
                onSetThumbnail={setThumbnailId}
              />
              {thumbnailId && (() => {
                const thumbImg = images.find((i) => i.id === thumbnailId);
                return thumbImg ? (
                  <Box mt={2} p={2} bgcolor="primary.50" borderRadius={2}
                    border="1px solid" borderColor="primary.light">
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar src={thumbImg.previewUrl} variant="rounded" sx={{ width: 48, height: 48 }} />
                      <Box>
                        <Typography fontSize={13} fontWeight={700}>Cover Image Set</Typography>
                        <Typography fontSize={11} color="text.secondary">
                          This image appears as the product thumbnail
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                ) : null;
              })()}
            </Section>

          </Stack>
        </TabPanel>

        {/* TAB 2 — Specs & Flags */}
        <TabPanel value={tab} index={2}>
          <Stack spacing={3}>
            <Section icon={<ViewModule />} title="Specifications"
              subtitle="Key-value pairs like Material, Dimensions, etc.">
              <SpecificationsEditor specs={specs} onChange={setSpecs} />
            </Section>

          </Stack>
        </TabPanel>

        {/* TAB 3 — Variants */}
        <TabPanel value={tab} index={3}>
          {!product.isProductVariants ? (
            <Paper variant="outlined" sx={{ p: 5, textAlign: "center", borderRadius: 2 }}>
              <Tune sx={{ fontSize: 48, color: "grey.300", mb: 1.5 }} />
              <Typography fontWeight={700} fontSize={16} mb={0.5}>Variants Not Enabled</Typography>
              <Typography color="text.secondary" fontSize={13} mb={2.5}>
                Enable "Has Variants" in the Basic Info tab to manage variant combinations.
              </Typography>
              <Button variant="contained" onClick={() => { setField("isProductVariants", true); setTab(0); }}>
                Enable Variants
              </Button>
            </Paper>
          ) : (
            <Stack spacing={3}>
              <AttributeSection attributes={attributes} onChange={setAttributes} />
              <Stack direction="row" spacing={2} alignItems="center">
                <Button variant="contained" startIcon={<AutoAwesome />} onClick={generateVariants}
                  disabled={!attributes.some((a) => a.values.length > 0)} sx={{ px: 3 }}>
                  Generate Combinations
                </Button>
                {variants.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {variants.length} combination{variants.length !== 1 ? "s" : ""} — existing data preserved on regenerate
                  </Typography>
                )}
              </Stack>
              {variants.length > 0 && (
                <VariantsTable
                  variants={variants}
                  onEdit={setEditVariant}
                  onDelete={(id) => setVariants((v) => v.filter((x) => x.id !== id))}
                  onBulkPrice={(p) => {
                    setVariants((v) => v.map((x) => ({ ...x, price: p })));
                    toast(`Price set to ₹${p} for all variants.`);
                  }}
                />
              )}
            </Stack>
          )}
        </TabPanel>

        {/* Bottom Actions */}
        <Box mt={3}>
          <Divider sx={{ mb: 2 }} />
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button color="inherit" variant="outlined" onClick={() => {
              pageChange(pageRoute.productList)
            }}>Cancel</Button>
            {/* <Button variant="contained" size="large" startIcon={<CheckCircle />}
              // onClick={handleSubmit} sx={{ px: 4 }}
              onClick={mode !== "add" ? handleUpdate : handleSubmit}
            >
              {mode != 'add' ? "Update Product" : "Save Product"}
            </Button> */}
            <Box mt={3}>
              {/* <Divider sx={{ mb: 2 }} /> */}

              <Stack direction="row" justifyContent="space-between" gap={10}>

                {/* LEFT SIDE */}
                <Button
                  variant="outlined"
                  disabled={tab === 0}
                  onClick={() => setTab((prev) => prev - 1)}
                >
                  Previous
                </Button>

                {/* RIGHT SIDE */}
                <Stack direction="row" spacing={2}>

                  {tab < 3 && (
                    <Button
                      variant="contained"
                      onClick={() => setTab((prev) => prev + 1)}
                    >
                      Next
                    </Button>
                  )}

                  {tab === 3 && (
                    <Button
                      variant="contained"
                      size="large"
                      disabled={loading}
                      startIcon={<CheckCircle />}
                      onClick={mode !== "add" ? handleUpdate : handleSubmit}
                    >
                      {mode !== "add" ? "Update Product" : "Submit Product"}
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Box>

          </Stack>
        </Box>



        {/* Variant Edit Dialog */}
        <VariantEditDialog
          open={!!editVariant}
          variant={editVariant}
          deletedVariantImages={deletedVariantImages}
          setDeletedVariantImages={setDeletedVariantImages}
          onClose={() => setEditVariant(null)}
          onSave={(updated) => setVariants((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))}
        />

        {/* Toast */}
        <Snackbar open={snack.open} autoHideDuration={3500}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
          <Alert severity={snack.severity} variant="filled"
            onClose={() => setSnack((s) => ({ ...s, open: false }))}>
            {snack.msg}
          </Alert>
        </Snackbar>
      </Box>
    </Paper>
  );
}